import { useEffect, useMemo, useState } from 'react';
import { uploadUrl, thumbUrls } from '../config/api';

const AVATAR_COLORS = ['#ffd8a8', '#b9e6cf', '#c6d4ff', '#ffd0df', '#d9c2ff', '#ffe8aa'];

function getInitials(name = 'NA') {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'NA';
  return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase();
}

function resolvePhotoPaths(photoPath) {
  const full = uploadUrl(photoPath);
  const thumbs = thumbUrls(photoPath);
  return full || thumbs.length ? { thumbs, full } : null;
}

function deriveFromRecord(record) {
  if (!record || typeof record !== 'object') return {};
  const name = record.name
    || [record.f_name, record.l_name].filter(Boolean).join(' ')
    || '';
  const seed = record.id ?? record.seed ?? 0;
  const photoPath = record.photo_relative_path ?? record.photoPath ?? null;
  return { name, seed, photoPath };
}

/**
 * Avatar primitive used everywhere. Pass `record` for any entity with
 * `{f_name, l_name, id, photo_relative_path}` or `{name, id, photo_relative_path}`
 * and it derives name/seed/photoPath automatically. Explicit props override.
 */
export default function SchoolAvatar({
  record,
  name,
  seed,
  large = false,
  className = '',
  photoPath,
  onEdit,
  enableViewer = false,
  useFullImage = false,
  allowFullFallback = false,
}) {
  const derived = deriveFromRecord(record);
  const resolvedName = name ?? derived.name ?? '';
  const resolvedSeed = seed ?? derived.seed ?? 0;
  const resolvedPhotoPath = photoPath ?? derived.photoPath ?? null;
  const photoPaths = useMemo(() => resolvePhotoPaths(resolvedPhotoPath), [resolvedPhotoPath]);
  const [thumbIndex, setThumbIndex] = useState(0);
  const [fullFailed, setFullFailed] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const photoKey = photoPaths ? [photoPaths.full, ...photoPaths.thumbs].filter(Boolean).join('|') : '';

  useEffect(() => {
    setThumbIndex(0);
    setFullFailed(false);
    setViewerOpen(false);
  }, [photoKey]);

  const classes = ['sap-avatar', large ? 'sap-avatar-large' : '', className].filter(Boolean).join(' ');
  const showingFull = useFullImage || (allowFullFallback && thumbIndex >= (photoPaths?.thumbs.length || 0));

  const activeSrc = photoPaths && !fullFailed
    ? (showingFull ? photoPaths.full : photoPaths.thumbs[thumbIndex])
    : null;

  const inner = activeSrc ? (
    <div className={classes} style={{ padding: 0, overflow: 'hidden' }}>
      <img
        src={activeSrc}
        alt=""
        loading="lazy"
        decoding="async"
        onError={() => {
          if (showingFull) {
            setFullFailed(true);
            return;
          }
          setThumbIndex((current) => current + 1);
        }}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </div>
  ) : (
    <div
      className={classes}
      style={{ background: AVATAR_COLORS[Math.abs(resolvedSeed) % AVATAR_COLORS.length] }}
    >
      {getInitials(resolvedName)}
    </div>
  );

  if (enableViewer && photoPaths?.full) {
    return (
      <>
        <button
          type="button"
          className="sap-avatar-button"
          onClick={() => setViewerOpen(true)}
          title="View photo"
        >
          {inner}
        </button>
        {viewerOpen ? (
          <div className="sap-photo-viewer-overlay" onClick={() => setViewerOpen(false)}>
            <div className="sap-photo-viewer" onClick={(event) => event.stopPropagation()}>
              <button type="button" className="sap-photo-viewer-close" onClick={() => setViewerOpen(false)}>
                x
              </button>
              <img src={photoPaths.full} alt="" />
            </div>
          </div>
        ) : null}
      </>
    );
  }

  if (!onEdit) return inner;

  return (
    <button
      type="button"
      onClick={onEdit}
      title="Edit photo"
      style={{
        position: 'relative',
        display: 'inline-block',
        padding: 0,
        border: 0,
        background: 'transparent',
        cursor: 'pointer',
      }}
    >
      {inner}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          right: -2,
          bottom: -2,
          width: 22,
          height: 22,
          borderRadius: 11,
          background: '#5b9fd8',
          color: '#fff',
          fontSize: 12,
          fontWeight: 700,
          lineHeight: '22px',
          textAlign: 'center',
          border: '2px solid #fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
        }}
      >
        ✎
      </span>
    </button>
  );
}
